import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon";
import { BRIEFING_VARS, OverviewBody, type Media } from "./PhoneOverview";
import { supabase } from "../lib/supabase";
import { WORKOUT_TREE_SELECT, fetchWorkoutTrees, type AssignedWorkoutTree } from "../lib/assignedWorkoutTree";
import { workoutOverview } from "../lib/workoutPreview";
import { exerciseIdsOf } from "../lib/phoneOverview";
import { exerciseLabels } from "../lib/exerciseLabels";
import { ExerciseLookWindow } from "./ExerciseLookWindow";
import { LIBRARY_ROWS_SELECT, readLibraryRows } from "../lib/libraryRows";
import { backToTopLabel, showsBackToTop } from "../lib/weekView";
import type { Said } from "../lib/carryFeedback";
import { AddExercise, LineField, RowMenu, useWeekEditor } from "./WeekEditor";
import { RowDrag } from "./RowDrag";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * W137 — ONE DAY COLUMN, AND ONE WORKOUT DRAWN IN IT (William's third look, 2026-10-02; Fable R3–R5, R5b, R8).
 *
 * His words: *"so just use these 7 rows, with straight lines but the content to look more beautiful, as it looks now in the
 * phone, workout overview. plus any comments though (coach notes) and rest times."* — and *"i think it's smarter to scroll
 * one day at a time so we don't have to scroll up again when we read the next day. and in all days, we can have a button
 * bottom right to go at the top with 1 click, in each day though separately."*
 *
 * ONE component for the column and ONE for the workout inside it, so the client Week and the Program board (cut 3) cannot
 * draw two different columns ("needs to look same"). The workout's content is `OverviewBody` — the phone overview's own
 * body, exported from `PhoneOverview.tsx`, never a second composition.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export type WeekTrees =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; trees: ReadonlyMap<string, AssignedWorkoutTree>; media: ReadonlyMap<string, Media>;
      /** W136 B2 S3 — each library row's `tracking_fields` (a run's unit is its field's), on the media read it already makes. */
      fields?: ReadonlyMap<string, readonly string[]>;
      /** QUEUE N2b-2 — each library row's equipment keys, read on the same call (the printed page's `Equipment:`). */
      equipment?: ReadonlyMap<string, readonly string[]> };

/**
 * 🔴 R4 — THE VISIBLE RANGE'S WORKOUTS IN ONE BATCHED READ (W107 fix (c)'s `fetchWorkoutTrees`, restored), then their
 * exercises' media in one batched read of the library rows (chunked at `batchedIn`'s fifty). Never one read per workout.
 * `ids` is the key: a new range is a new read; the same range re-drawn is not. `enabled` false (Minimized) reads NOTHING —
 * the square cards stay what W115 made them.
 */
export function useWeekTrees(ids: readonly string[], enabled: boolean, reloadKey = 0): WeekTrees {
  const key = enabled ? ids.join(",") : "";
  const [state, setState] = useState<WeekTrees>({ status: "idle" });
  useEffect(() => {
    if (key === "") { setState({ status: "idle" }); return; }
    let cancelled = false;
    /* Week editor stage 1 — a re-read after a write keeps drawing the trees it has (the editor's written trees sit on top);
       "Loading…" is only for a week never read yet, so moving a row never blanks the week. */
    setState((s) => (s.status === "ready" ? s : { status: "loading" }));
    (async () => {
      const wanted = key.split(",");
      const r = await fetchWorkoutTrees(
        (chunkIds) => supabase.from("coach_workouts").select(WORKOUT_TREE_SELECT).in("id", chunkIds), wanted);
      if (cancelled) return;
      if (r.error !== null) { setState({ status: "error", message: r.error }); return; }
      const exerciseIds = [...r.trees.values()].flatMap((t) => exerciseIdsOf(t));
      // W136 B2 S3r4 W1 — the ONE library read (`readLibraryRows`), shared with the overview window.
      const { media, fields, equipment } = await readLibraryRows(
        (part) => supabase.from("coach_exercises").select(LIBRARY_ROWS_SELECT).in("id", part), exerciseIds);
      if (cancelled) return;
      setState({ status: "ready", trees: r.trees, media, fields, equipment });
    })();
    return () => { cancelled = true; };
  }, [key, reloadKey]);
  return state;
}

/**
 * One day: its head (the caller's — weekday, date, mark, `+`), then its workouts. With `scrolls`, the body is the day's OWN
 * scroll container — reading Tuesday never moves Monday — focusable so the arrow keys scroll it, named for the day; and the
 * `↑` appears at its bottom-right only while it is scrolled away from its top, scrolling THAT column alone.
 */
export function DayColumn({ dayKey, label, topLabel, head, scrolls, className, rootRef, children, dropDay = false }: {
  /** Week editor stage 1 — a row dragged onto this column lands on this day. */
  dropDay?: boolean;
  dayKey: string;
  /** The day in words for the scroll region's name (`Tuesday 29/9`). */
  label: string;
  head: ReactNode;
  scrolls: boolean;
  className?: string;
  /** W137 cut 2 — the day drag's drop target is the WHOLE column (`DropDay` hands its ref here). */
  rootRef?: (el: HTMLElement | null) => void;
  /** The `↑`'s name when the day is not a calendar date (the Program board: `Back to top of Tuesday, week 2`). */
  topLabel?: string;
  children: ReactNode;
}) {
  const body = useRef<HTMLDivElement>(null);
  const [away, setAway] = useState(false);
  return (
    <div ref={rootRef} className={`week-col${className === undefined ? "" : ` ${className}`}`} data-drop-day={dropDay ? dayKey : undefined}>
      <div className="week-col-head">{head}</div>
      <div ref={body} className="week-col-list"
           /* Only a scroll container needs to be a stop of its own; on the page-scroll layouts it is plain content. */
           tabIndex={scrolls ? 0 : undefined} role={scrolls ? "region" : undefined}
           aria-label={scrolls ? label : undefined}
           onScroll={scrolls ? (e) => { setAway(showsBackToTop(e.currentTarget.scrollTop)); } : undefined}>
        {children}
      </div>
      {scrolls && away && (
        <button type="button" className="week-col-top" aria-label={topLabel ?? backToTopLabel(dayKey)} title={topLabel ?? backToTopLabel(dayKey)}
                onClick={() => { if (body.current !== null) backToStart(body.current, "column"); }}>
          <Icon name="arrow-up" size={16} stroke={2} />
        </button>
      )}
    </div>
  );
}

/**
 * The `↑`'s one motion: a COLUMN scrolls itself to its top (and takes focus, so the arrow keys keep reading it); the PAGE
 * brings the week's top into view. Smooth unless the viewer asked for reduced motion (Apple's rule, `docs/ARCHITECTURE.md`).
 */
export function backToStart(el: HTMLElement, what: "column" | "page"): void {
  const behavior: ScrollBehavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
  if (what === "column") {
    el.scrollTo({ top: 0, behavior });
    el.focus({ preventScroll: true });
  } else {
    el.scrollIntoView({ block: "start", behavior });
  }
}

/**
 * One workout in a column: the phone overview's content without the phone (`OverviewBody`), its 34-pt title replaced by the
 * column's own title line (`title`, which carries the workout's actions), plus each exercise's rest and coach note.
 * Three states, said as three: loading, a refused read (the server's own words), and the drawn workout.
 */
export function ColumnWorkout({ title, tree, trees, onEditSets, notes = true, said, workoutSaid }: {
  /** CARRY-FEEDBACK B — the coach's last review of this same workout, when the client will meet it HERE. */
  workoutSaid?: Said;
  title: ReactNode;
  /** CARRY-FEEDBACK A — row id → the coach's last comment the client will meet on that row (`useCarryFeedback`). */
  said?: ReadonlyMap<string, Said>;
  /** His 2026-10-15 switch: the coach's exercise notes drawn under the rest (true, the default) or left off the column. */
  notes?: boolean;
  /** R3b — `Edit sets` in the exercise's look window: the page's per-day set editor for that row. */
  onEditSets?: (exerciseRowId: string) => void;
  /** The workout's id in `trees`; absent = nothing to draw (a task, or a row the page holds no id for). */
  tree: string | undefined;
  trees: WeekTrees;
}) {
  /* Week editor stage 1 — where the Week is editable, the tree drawn is the editor's (what was last written), else the read. */
  const ed = useWeekEditor();
  const t = tree === undefined ? undefined : ed?.treeOf(tree) ?? (trees.status !== "ready" ? undefined : trees.trees.get(tree));
  const fields = trees.status === "ready" ? trees.fields : undefined;
  const editable = ed !== null && tree !== undefined && !ed.locked(tree);
  const rowEdit = useMemo(() => (!editable || tree === undefined ? undefined : {
    wrap: (rowId: string, row: ReactNode) => <RowDrag workoutId={tree} rowId={rowId}>{row}</RowDrag>,
    lead: (rowId: string, label: ReactNode) => (
      <span className="week-lead">
        {label}
        <RowMenu workoutId={tree} rowId={rowId} />
      </span>
    ),
    body: (rowId: string, drawn: ReactNode) => <LineField workoutId={tree} rowId={rowId} drawn={drawn} />,
  }), [editable, tree]);
  const preview = useMemo(() => (t === undefined ? null : workoutOverview(t, fields)), [t, fields]);
  const labels = useMemo(() => (preview === null ? undefined : exerciseLabels(preview.sections)), [preview]);
  /* R3b point 3 — the exercise NAME opens it to be SEEN (`ExerciseLookWindow`); focus goes back to the name on close. */
  const [look, setLook] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const lookRow = look === null || preview === null ? undefined
    : preview.sections.flatMap((s) => s.rows).find((r) => r.exerciseRowId === look);
  function closeLook(): void {
    setLook(null);
    const el = opener.current;
    opener.current = null;
    requestAnimationFrame(() => { el?.focus(); });
  }
  return (
    <div className="week-col-workout" style={BRIEFING_VARS} data-drop-workout={editable ? tree : undefined}>
      {preview !== null && trees.status === "ready" ? (
        <>
          <OverviewBody preview={preview} chips={[]} media={trees.media} head={title} extended notes={notes} said={said} workoutSaid={workoutSaid} labels={labels} rowEdit={rowEdit}
                        onPickRow={(rowId) => { opener.current = document.activeElement as HTMLElement | null; setLook(rowId); }} />
          {editable && <AddExercise workoutId={tree} />}
        </>
      ) : (
        <>
          {title}
          {tree !== undefined && trees.status === "loading" && <p className="phone-note" aria-live="polite">Loading…</p>}
          {tree !== undefined && trees.status === "error" && <p className="phone-note" role="alert">{trees.message}</p>}
        </>
      )}
      {lookRow !== undefined && (
        <ExerciseLookWindow exerciseId={lookRow.exerciseId} label={labels?.get(lookRow.exerciseRowId)?.label ?? null}
          name={lookRow.name} dose={lookRow.phoneDose} rest={lookRow.rest} note={lookRow.note} onClose={closeLook}
          onEditSets={onEditSets === undefined ? undefined : () => {
            const id = lookRow.exerciseRowId;
            opener.current = null;
            setLook(null);
            onEditSets(id);
          }} />
      )}
    </div>
  );
}
